import React, { useState } from "react";
import { freeOver } from "@/lib/storenav";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Star, Minus, Plus, ChevronRight, ShieldCheck, Truck, RotateCcw } from "lucide-react";
import { useStore } from "@/context/StoreContext";
import { useCart } from "@/context/CartContext";
import StoreShell from "@/components/StoreShell";
import ProductGallery from "@/components/storefront/ProductGallery";
import { Splash, ErrorView, ProductCard } from "./Storefront";
import { inr, rating, reviews } from "@/lib/storeutils";
import { variantLabel, fromPrice } from "@/lib/variants";

export default function ProductDetail() {
  const { productId } = useParams();
  const s = useStore();
  const cart = useCart();
  const nav = useNavigate();
  const [variant, setVariant] = useState({});   // shopper's own choices; anything untouched is auto-selected below
  const [pick, setPick] = useState({});      // chosen value per variant option, keyed by option name
  const [qty, setQty] = useState(1);

  if (s.loading) return <Splash />;
  if (s.error) return <ErrorView error={s.error} />;

  const product = s.products.find((p) => p.id === productId);
  if (!product) return <ErrorView error="Product not found" />;

  const { c, font, border, muted, cardShadow, slug } = s;
  // New-style variants: each combination has its own price, stock, SKU and image.
  const vTypes = product.option_types || [];
  const vItems = product.variant_items || [];
  const useVar = vItems.length > 0;
  // Auto-select: keep what the shopper chose, otherwise pick the first value that is still in stock
  // given the options before it, so Add to Cart / Buy Now are usable straight away.
  const eff = {};
  vTypes.forEach((t) => {
    const fits = (val) => vItems.some((v) => v.stock > 0 && v.options[t.name] === val && vTypes.every((o) => eff[o.name] === undefined || v.options[o.name] === eff[o.name]));
    const mine = pick[t.name];
    eff[t.name] = mine && fits(mine) ? mine : t.values.find(fits) ?? mine ?? t.values[0];
  });
  const chosen = useVar ? eff : pick;
  const chosenLegacy = {};
  (product.variants || []).forEach((v) => { chosenLegacy[v.name] = variant[v.name] || v.options[0]; });
  const selected = useVar && vTypes.every((t) => chosen[t.name]) ? vItems.find((v) => vTypes.every((t) => v.options[t.name] === chosen[t.name])) : null;
  const available = (t, val) => vItems.some((v) => v.stock > 0 && v.options[t.name] === val && vTypes.every((o) => o.name === t.name || !chosen[o.name] || v.options[o.name] === chosen[o.name]));
  const unitPrice = selected ? selected.price : useVar ? fromPrice(product) : product.price;
  const unitCompare = selected ? selected.compare_at_price : useVar ? null : product.compare_at_price;
  const stockNow = useVar ? (selected ? selected.stock : null) : product.stock;
  const priceRange = useVar && !selected && new Set(vItems.map((v) => v.price)).size > 1;
  const variantStr = useVar ? (selected ? variantLabel(selected.options, vTypes) : null)
    : product.variants?.map((v) => chosenLegacy[v.name]).filter(Boolean).join(" / ") || null;
  const ready = useVar ? !!selected && selected.stock > 0 : !product.variants?.length || product.variants.every((v) => chosenLegacy[v.name]);
  const cartExtra = selected ? { price: selected.price, variant_id: selected.id, sku: selected.sku, image: selected.image || product.image } : {};
  const related = s.products.filter((p) => p.category === product.category && p.id !== product.id).slice(0, 4);
  const off = unitCompare ? Math.round((1 - unitPrice / unitCompare) * 100) : 0;

  const addToCart = () => { cart.add(product, variantStr, qty, cartExtra); toast.success("Added to cart"); cart.setOpen(true); };
  const buyNow = () => { cart.add(product, variantStr, qty, cartExtra); nav(`/store/${slug}/checkout`); };

  return (
    <StoreShell>
      <div className="max-w-6xl mx-auto px-5 py-6">
        <nav className="text-sm mb-5" style={{ color: muted }}>
          <Link to={`/store/${slug}`} className="hover:opacity-70">Home</Link> <span className="opacity-50">/</span> <span>{product.category || "Products"}</span> <span className="opacity-50">/</span> <span style={{ color: c.text }}>{product.name}</span>
        </nav>

        <div className="grid md:grid-cols-2 gap-8" data-testid="product-detail">
          <ProductGallery images={[product.image, ...(product.images || [])]} preferred={selected?.image} name={product.name} c={c} border={border} muted={muted} />

          <div>
            {product.brand && <div className="text-xs font-bold uppercase tracking-widest" style={{ color: c.accent }}>{product.brand}</div>}
            <h1 className="text-3xl font-extrabold mt-1" style={{ fontFamily: font }}>{product.name}</h1>
            <div className="flex items-center gap-2 mt-2">
              <div className="flex items-center gap-1"><Star size={15} fill={c.accent} color={c.accent} /><span className="text-sm font-semibold">{rating(product.id).toFixed(1)}</span></div>
              <span className="text-xs" style={{ color: muted }}>({reviews(product.id)} reviews)</span>
              {product.sku && <span className="text-xs" style={{ color: muted }}>· SKU: {product.sku}</span>}
            </div>
            <div className="flex items-center gap-3 mt-4">
              {priceRange && <span className="text-sm" style={{ color: muted }}>From</span>}
              <span className="text-3xl font-extrabold" style={{ color: c.primary }} data-testid="product-price">{inr(unitPrice)}</span>
              {unitCompare ? <span className="text-lg line-through" style={{ color: muted }}>{inr(unitCompare)}</span> : null}
              {off > 0 && <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: c.accent, color: "#fff" }}>{off}% OFF</span>}
            </div>
            {stockNow === null
              ? <div className="text-sm mt-2" style={{ color: muted }}>Select {vTypes.map((t) => t.name).join(" and ")} to see availability</div>
              : <div className="text-sm mt-2" style={{ color: stockNow > 0 ? "#16a34a" : "#dc2626" }} data-testid="product-stock">{stockNow > 0 ? `In stock (${stockNow} available)` : "Out of stock"}</div>}
            <p className="mt-4 text-sm leading-relaxed whitespace-pre-line" style={{ color: muted }}>{product.description || "No description available."}</p>

            {useVar && vTypes.map((t) => (
              <div key={t.name} className="mt-5" data-testid={`variant-option-${t.name}`}>
                <div className="text-xs uppercase tracking-widest mb-1.5 font-semibold" style={{ color: muted }}>{t.name}</div>
                <div className="flex gap-2 flex-wrap">
                  {t.values.map((val) => {
                    const on = chosen[t.name] === val;
                    const ok = available(t, val);
                    return (
                      <button key={val} type="button" onClick={() => setPick({ ...pick, [t.name]: val })} data-testid={`vopt-${t.name}-${val}`} aria-pressed={on}
                        className="px-4 py-2 rounded-lg text-sm border transition-all"
                        style={on ? { background: c.primary, color: "#fff", borderColor: c.primary } : { borderColor: border, color: ok ? c.text : muted, opacity: ok ? 1 : 0.5, textDecoration: ok ? "none" : "line-through" }}>{val}</button>
                    );
                  })}
                </div>
              </div>
            ))}

            {!useVar && product.variants?.map((v) => (
              <div key={v.name} className="mt-5">
                <div className="text-xs uppercase tracking-widest mb-1.5 font-semibold" style={{ color: muted }}>{v.name}</div>
                <div className="flex gap-2 flex-wrap">
                  {v.options.map((o) => (
                    <button key={o} onClick={() => setVariant({ ...variant, [v.name]: o })} data-testid={`variant-${v.name}-${o}`} className="px-4 py-2 rounded-lg text-sm border transition-all"
                      style={chosenLegacy[v.name] === o ? { background: c.primary, color: "#fff", borderColor: c.primary } : { borderColor: border, color: c.text }}>{o}</button>
                  ))}
                </div>
              </div>
            ))}

            {product.attributes?.length > 0 && (
              <dl className="mt-5 grid grid-cols-[auto_1fr] gap-x-5 gap-y-1.5 text-sm" data-testid="product-specs">
                {product.attributes.map((a, i) => (
                  <React.Fragment key={i}><dt style={{ color: muted }}>{a.name}</dt><dd className="font-medium">{a.value}</dd></React.Fragment>
                ))}
              </dl>
            )}

            <div className="flex items-center gap-3 mt-6">
              <div className="flex items-center rounded-full border" style={{ borderColor: border }}>
                <button onClick={() => setQty(Math.max(1, qty - 1))} className="p-2.5" aria-label="Decrease"><Minus size={15} /></button>
                <span className="px-3 font-mono" data-testid="product-qty">{qty}</span>
                <button onClick={() => setQty(stockNow ? Math.min(stockNow, qty + 1) : qty + 1)} className="p-2.5" aria-label="Increase"><Plus size={15} /></button>
              </div>
            </div>

            <div className="flex gap-3 mt-4">
              <button disabled={!ready || (!useVar && product.stock <= 0)} onClick={addToCart} data-testid="product-detail-add-to-cart"
                className="flex-1 py-3 rounded-full font-bold border transition-opacity hover:opacity-90 disabled:opacity-40" style={{ borderColor: c.primary, color: c.primary }}>Add to Cart</button>
              <button disabled={!ready || (!useVar && product.stock <= 0)} onClick={buyNow} data-testid="product-detail-buy-now"
                className="flex-1 py-3 rounded-full font-bold transition-opacity hover:opacity-90 disabled:opacity-40 flex items-center justify-center gap-2" style={{ background: c.accent, color: "#fff" }}>Buy Now <ChevronRight size={17} /></button>
            </div>
            {!ready && <p className="text-xs mt-2" style={{ color: muted }}>Please select all options to continue.</p>}

            <div className="grid grid-cols-3 gap-3 mt-6">
              {[[Truck, freeOver(s.store) > 0 ? `Free shipping ₹${freeOver(s.store).toLocaleString("en-IN")}+` : "Tracked delivery"], [ShieldCheck, "Secure payment"], [RotateCcw, "Easy returns"]].map(([Icon, t]) => (
                <div key={t} className="flex items-center gap-2 text-xs" style={{ color: muted }}><Icon size={16} style={{ color: c.primary }} /> {t}</div>
              ))}
            </div>
          </div>
        </div>

        {related.length > 0 && (
          <div className="mt-14">
            <h2 className="text-2xl font-extrabold mb-6" style={{ fontFamily: font }}>You may also like</h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
              {related.map((p) => <ProductCard key={p.id} p={p} s={s} cart={cart} />)}
            </div>
          </div>
        )}
      </div>
    </StoreShell>
  );
}
